import React, { useCallback, useEffect, useState, useContext } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
   Breadcrumbs,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  Stack,

  TableRow,
  Tooltip,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import RefreshIcon from "@mui/icons-material/Refresh";
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TableFilterBar from '../components/Table/TableFilterBar';
import { request } from "../utils/request";
import { ColorModeContext } from "../index";
import ReusableTable from "../components/Table/ReusableTable";
import ReusableHeader from "../components/Header/ReusableHeader";
import { hasFeatureAccess, getStoredUser, FEATURES } from "../utils/rbac";
import AccessDenied from "../components/guards/AccessDenied";

/* =========================================================
   mEDGE design tokens (mirrors global.css :root values).
   Same variable names as global.css, remapped for dark mode.
   Applied via style={V} on the page root and on the Select
   menu's PaperProps — Select renders its dropdown in a portal
   outside this component's DOM tree and otherwise wouldn't
   inherit vars.
   ========================================================= */

const LIGHT_VARS = {
  "--vault-navy": "#0B2545",
  "--edge-blue": "#1F6FEB",
  "--canvas": "#FFFFFF",
  "--mist": "#F7F9FC",
  "--hairline": "#E4E9F0",
  "--blue-tint": "#EAF2FE",
  "--slate": "#6B7A90",
  "--secure": "#00A88F",
  "--high": "#E2622B",
  "--elevated": "#F0A202",
  "--critical": "#D6323A",
  "--border-radius": "6px",
};

const DARK_VARS = {
  "--vault-navy": "#F4F7FB",
  "--edge-blue": "#4C9AFF",
  "--canvas": "#0F1729",
  "--mist": "#252b3b",
  "--hairline": "rgba(255,255,255,0.14)",
  "--blue-tint": "rgba(76,154,255,0.16)",
  "--slate": "#93A4BD",
  "--secure": "#2FD3B8",
  "--high": "#F07C46",
  "--elevated": "#FFC24B",
  "--critical": "#FF6B72",
  "--border-radius": "6px",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const POLL_INTERVAL_MS = 30000;

function formatDate(value) {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

// Maps a device status to a mEDGE security-signal chip class.
function statusChipClass(status) {
  if (status === "ACTIVE") return "global-chip signal-secure-bg";
  if (status === "DISABLED") return "global-chip signal-elevated-bg";
  return "global-chip signal-critical-bg";
}

export default function WindowsAgentDevices() {
  const { mode } = useContext(ColorModeContext || { mode: "light" });
  const V = mode === "dark" ? DARK_VARS : LIGHT_VARS;

  const user = getStoredUser();
  const canRead = hasFeatureAccess(user, FEATURES.WINDOWS_AGENT_DEVICES, 'read');
  const canUpdate = hasFeatureAccess(user, FEATURES.WINDOWS_AGENT_DEVICES, 'update');

  const [devices, setDevices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updatingDeviceId, setUpdatingDeviceId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialStatus = () => {
    const s = searchParams.get("status");
    if (s === "offline" || s === "inactive") return "Offline";
    if (s === "offline_7d" || s === "stale") return "Offline (>7d)";
    if (s === "online" || s === "active") return "Online";
    return "";
  };

  const [statusFilter, setStatusFilter] = useState(getInitialStatus);

  useEffect(() => {
    const s = searchParams.get("status");
    if (s === "offline" || s === "inactive") {
      setStatusFilter("Offline");
    } else if (s === "offline_7d" || s === "stale") {
      setStatusFilter("Offline (>7d)");
    } else if (s === "online" || s === "active") {
      setStatusFilter("Online");
    } else if (s === null) {
      setStatusFilter("");
    }
  }, [searchParams]);

  const filteredDevices = React.useMemo(() => {
    let result = devices;

    if (statusFilter === "Offline") {
      result = result.filter(d => !d.isLive);
    } else if (statusFilter === "Offline (>7d)") {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      result = result.filter(d => !d.isLive && (!d.lastSeenAt || new Date(d.lastSeenAt) < sevenDaysAgo));
    } else if (statusFilter === "Online") {
      result = result.filter(d => Boolean(d.isLive));
    }

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(d => 
        (d.hostname || "").toLowerCase().includes(lower) || 
        (d.deviceId || "").toLowerCase().includes(lower) ||
        (d.osName || "").toLowerCase().includes(lower) ||
        (d.osBuild || "").toLowerCase().includes(lower)
      );
    }
    return result;
  }, [devices, searchQuery, statusFilter]);

  // Select popover menu — portaled, so it needs its own var scope + card class.
  const selectMenuProps = {
    PaperProps: {
      className: "global-card",
      style: V,
      sx: {
        mt: 0.5,
        "& .MuiList-root": { padding: 0 },
        "& .MuiMenuItem-root": {
          fontFamily: "Calibri, Arial, sans-serif",
          fontSize: 17,
          color: "var(--vault-navy)",
        },
        "& .MuiMenuItem-root:hover": {
          backgroundColor: "var(--mist)",
        },
        "& .Mui-selected": {
          backgroundColor: "var(--blue-tint) !important",
        },
      },
    },
  };

  const loadDevices = useCallback(async (showLoading = true) => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    if (showLoading) setLoading(true);
    try {
      const response = await request(
        `admin/agent-devices?page=${page + 1}&limit=${rowsPerPage}`,
        { method: "GET" }
      );
      setDevices(Array.isArray(response?.data) ? response.data : []);
      setTotal(Number(response?.pagination?.total || 0));
    } catch (error) {
      setFeedback({ severity: "error", message: error?.message || "Unable to load Windows agent devices." });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    const interval = window.setInterval(() => loadDevices(false), POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [loadDevices]);

  const updatePolicy = async (deviceId, mfaPolicy) => {
    const previous = devices.find((device) => device.deviceId === deviceId)?.mfaPolicy;
    setUpdatingDeviceId(deviceId);
    try {
      const response = await request(
        `admin/agent-devices/${encodeURIComponent(deviceId)}/policy`,
        { method: "PATCH", body: { mfaPolicy } }
      );
      const updated = response?.data;
      if (!updated) throw new Error("The server returned no updated device.");
      setDevices((current) => current.map((device) => (
        device.deviceId === deviceId ? { ...device, ...updated } : device
      )));
      setFeedback({ severity: "success", message: `MFA policy updated for ${deviceId}.` });
    } catch (error) {
      setDevices((current) => current.map((device) => (
        device.deviceId === deviceId ? { ...device, mfaPolicy: previous || device.mfaPolicy || "TOTP" } : device
      )));
      setFeedback({ severity: "error", message: error?.message || "Unable to update MFA policy." });
    } finally {
      setUpdatingDeviceId(null);
    }
  };

  if (!canRead) {
    return <AccessDenied featureName="Windows Agent Devices" title="Windows Agent Devices" />;
  }

  return (
    
      <Box p={2} style={V}>
      <ReusableHeader
        title="Windows Agent Devices"
        breadcrumbs={[{ label: "Windows Agent Devices" }]}
      />

      <Box sx={{ mt: 3 }}>
        <TableFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={[
            {
              id: "status",
              label: "Status",
              options: ["Online", "Offline", "Offline (>7d)"],
              value: statusFilter,
            },
          ]}
          onFilterChange={(id, val) => {
            setStatusFilter(val);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              if (val === "Offline") next.set("status", "offline");
              else if (val === "Offline (>7d)") next.set("status", "offline_7d");
              else if (val === "Online") next.set("status", "online");
              else next.delete("status");
              return next;
            }, { replace: true });
          }}
          searchPlaceholder="Search Windows Devices..."
        />

      <ReusableTable
        columns={[
          {
            id: "hostname", 
            label: "Hostname", 
            render: (row) => (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--blue-tint)', color: 'var(--edge-blue)' }}>
                  <DesktopWindowsIcon fontSize="small" />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--vault-navy)' }}>
                  {row.hostname || "Unknown"}
                </Typography>
              </Stack>
            )
          },
          {
            id: "isLive",
            label: "Status",
            render: (row) => (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: row.isLive ? 'var(--secure)' : 'var(--critical)' }}>
                {row.isLive ? <CheckCircleOutlineIcon fontSize="small" /> : <CancelOutlinedIcon fontSize="small" />}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {row.isLive ? "Online" : "Offline"}
                </Typography>
              </Stack>
            )
          },
          { id: "osBuild", label: "OS / Build", render: (row) => [row.osName, row.osVersion, row.osBuild].filter(Boolean).join(" / ") || "Unknown" },
          { id: "architecture", label: "Architecture", render: (row) => row.architecture || "Unknown" },
          { id: "agentVersion", label: "Agent Version", render: (row) => row.agentVersion || "Unknown" },
          { id: "lastSeenAt", label: "Last Seen", render: (row) => formatDate(row.lastSeenAt) },
          { 
            id: "status", 
            label: "Registration", 
            render: (row) => {
              const isActive = row.status === "ACTIVE";
              const color = isActive ? 'var(--secure)' : row.status === "DISABLED" ? 'var(--elevated)' : 'var(--critical)';
              const Icon = isActive ? CheckCircleOutlineIcon : CancelOutlinedIcon;
              return (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color }}>
                  <Icon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.status || "UNKNOWN"}</Typography>
                </Stack>
              );
            } 
          },
          {
            id: "mfaPolicy",
            label: "MFA Policy",
            render: (row) => (
              <FormControl size="small" className="global-input global-form-field" sx={{ minWidth: 105 }}>
                <Select
                  value={row.mfaPolicy || "TOTP"}
                  onChange={(event) => updatePolicy(row.deviceId, event.target.value)}
                  disabled={!canUpdate || updatingDeviceId === row.deviceId}
                  aria-label={`MFA policy for ${row.hostname || row.deviceId}`}
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="TOTP">TOTP</MenuItem>
                  <MenuItem value="NO_MFA">NO MFA</MenuItem>
                </Select>
              </FormControl>
            )
          }
        ]}
        data={filteredDevices}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        emptyMessage="No Windows agent devices found."
      />
      </Box>

      <Snackbar open={Boolean(feedback)} autoHideDuration={5000} onClose={() => setFeedback(null)}>
        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            className="global-alert"
            style={V}
            sx={{ backgroundColor: "var(--canvas)", color: "var(--vault-navy)", border: "1px solid var(--hairline)" }}
          >
            {feedback.message}
          </Alert>
        ) : null}
      </Snackbar>
      </Box>
     
    
  );
}